import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, CircularProgress, TextField } from "@mui/material";
import Topbar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import EmptyState from "../../../../../../component/EmptyState";
import InlineMessage from "../../../../../../component/InlineMessage";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../../component/Modal";
import { StatusChip, PersonCell } from "../../../../../../component/listing";
import { useNotify } from "../../../../../../component/NotificationProvider";
import {
  getDocumentQueue, verifyDocument, returnDocument, getDocumentFileUrl, DOCUMENT_TYPE_LABELS,
} from "../../../../../../services/documents";
import { tokens } from "../../../../../../theme";

const TABS = [
  { status: "submitted", label: "To verify" },
  { status: "verified", label: "Verified" },
  { status: "returned", label: "Returned" },
];

const day = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

/**
 * Documents to verify.
 *
 * HR verifies every document an employee hands in — from onboarding or
 * later, a renewed passport or a new address proof. Until verified it does
 * not count: an onboarding item it was collected for stays open. Returning
 * one needs a reason, because the employee has to know what to fix.
 */
const DocumentVerification = () => {
  const notify = useNotify();
  const [tab, setTab] = useState("submitted");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [returning, setReturning] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setRows(null);
    setError("");
    const res = await getDocumentQueue(tab);
    if (res?.success === false) {
      setError(res?.message || "Could not load documents");
      setRows([]);
    } else {
      setRows(Array.isArray(res?.data) ? res.data : []);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Opened on the click and pointed at the file when the signed link arrives:
  // a tab opened after a request is a pop-up as far as Safari is concerned.
  const view = async (doc) => {
    const win = window.open("", "_blank");
    const res = await getDocumentFileUrl(doc._id).catch(() => null);
    if (res?.data?.url && win) win.location.href = res.data.url;
    else {
      win?.close();
      notify.error(res?.message || "This document could not be opened");
    }
  };

  const verify = async (doc) => {
    setBusy(doc._id);
    const res = await verifyDocument(doc._id);
    setBusy(null);
    if (res?.success === false) return notify.error(res?.message || "Could not verify");
    notify.success(`${doc.label} verified`);
    load();
  };

  const sendBack = async () => {
    if (!reason.trim()) return;
    setBusy(returning._id);
    const res = await returnDocument(returning._id, reason.trim());
    setBusy(null);
    if (res?.success === false) return notify.error(res?.message || "Could not return it");
    notify.success(`${returning.label} returned`);
    setReturning(null);
    setReason("");
    load();
  };

  const name = (e) => `${e?.fname || ""} ${e?.lname || ""}`.trim() || "Employee";

  return (
    <>
      <Topbar title="Documents to verify" />
      <MainBox>
        <Stack direction="row" spacing={0.5} sx={{ mb: "1.25rem" }}>
          {TABS.map((t) => (
            <Box
              key={t.status}
              component="button"
              type="button"
              aria-pressed={tab === t.status}
              onClick={() => setTab(t.status)}
              sx={{
                font: "inherit", fontSize: "0.8125rem", fontWeight: 600, px: "0.75rem", py: "0.375rem",
                borderRadius: "8px", cursor: "pointer",
                border: `1px solid ${tab === t.status ? "transparent" : tokens.border}`,
                background: tab === t.status ? tokens.primary : "transparent",
                color: tab === t.status ? tokens.onPrimary : tokens.textMuted,
                "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
              }}
            >
              {t.label}
            </Box>
          ))}
        </Stack>

        {error && (
          <InlineMessage tone="danger" title="Could not load documents" sx={{ mb: "1.25rem" }}>{error}</InlineMessage>
        )}

        {rows === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "3rem" }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <EmptyState
            title={tab === "submitted" ? "Nothing waiting" : `Nothing ${tab}`}
            body={tab === "submitted"
              ? "Documents employees hand in — at onboarding or later — appear here until you verify or return them."
              : "Documents appear here once they have been decided."}
          />
        ) : (
          <Stack spacing={1.25}>
            {rows.map((doc) => (
              <Stack
                key={doc._id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ border: `1px solid ${tokens.border}`, borderRadius: "10px", padding: "1rem 1.25rem", background: tokens.card }}
              >
                <Box sx={{ width: "14rem", flexShrink: 0 }}>
                  <PersonCell name={name(doc.employee)} secondary={doc.employee?.aliasId} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontWeight: 600, fontSize: "0.9375rem", color: tokens.text }}>{doc.label}</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
                    {[
                      DOCUMENT_TYPE_LABELS[doc.type] !== doc.label ? DOCUMENT_TYPE_LABELS[doc.type] : null,
                      `Submitted ${day(doc.createdAt)}`,
                      doc.expiresAt ? `expires ${day(doc.expiresAt)}` : null,
                      doc.status === "returned" && doc.returnReason ? `returned: ${doc.returnReason}` : null,
                    ].filter(Boolean).join(" · ")}
                  </Box>
                  {doc.type === "aadhaar_masked" && doc.status === "submitted" && (
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.warningText, mt: "0.25rem" }}>
                      Check only the last 4 digits are visible. Return it if the full number shows.
                    </Box>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  {doc.status !== "submitted" && (
                    <StatusChip label={doc.status === "verified" ? "Verified" : "Returned"} tone={doc.status === "verified" ? "success" : "danger"} />
                  )}
                  <BtnSecondary onClick={() => view(doc)} sx={{ borderRadius: "8px" }}>View</BtnSecondary>
                  {doc.status === "submitted" && (
                    <>
                      <BtnSecondary disabled={busy === doc._id} onClick={() => { setReturning(doc); setReason(""); }} sx={{ borderRadius: "8px" }}>
                        Return
                      </BtnSecondary>
                      <BtnPrimary disabled={busy === doc._id} onClick={() => verify(doc)} sx={{ borderRadius: "8px", minWidth: "6rem" }}>
                        {busy === doc._id ? "Saving…" : "Verify"}
                      </BtnPrimary>
                    </>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </MainBox>

      <Modal open={Boolean(returning)} onClose={() => setReturning(null)} width="30rem">
        <ModalHead
          title={`Return ${returning?.label || "this document"}?`}
          subtitle={returning ? name(returning.employee) : ""}
          onClose={() => setReturning(null)}
        />
        <ModalBody>
          <Box sx={{ fontSize: "0.875rem", color: tokens.text, lineHeight: 1.6, mb: "1rem" }}>
            They are told what you write here and asked to send it again.
          </Box>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="What needs fixing"
            placeholder="e.g. The photo is blurry — the number is unreadable"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={() => setReturning(null)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary disabled={!reason.trim() || busy === returning?._id} onClick={sendBack} sx={{ borderRadius: "8px" }}>
            Return it
          </BtnPrimary>
        </ModalFoot>
      </Modal>
    </>
  );
};

export default DocumentVerification;
